import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Activity, Loader2, KeyRound, LogOut } from "lucide-react";
import Footer from "@/components/Footer";

export default function ForcePasswordChange() {
  const { user, updateUser, logout } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { new_password: password });
      const { data } = await api.get("/auth/me");
      updateUser(data);
      toast.success("Password updated — welcome aboard!");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6">
      <div className="w-full max-w-md animate-fade-up">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center">
            <Activity className="h-6 w-6 text-white" />
          </div>
          <span className="font-heading text-xl font-bold">ABS Pulse</span>
        </div>

        <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-5">
          <KeyRound className="h-6 w-6" />
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Set your password</h1>
        <p className="text-muted-foreground mt-2 mb-8">
          Hi {user.name}, you're signed in with a temporary password. Choose a new one to continue.
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="force-password-form">
          <div className="space-y-2">
            <Label htmlFor="new-pw">New password</Label>
            <Input
              id="new-pw"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              required
              data-testid="new-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-pw">Confirm password</Label>
            <Input
              id="confirm-pw"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter your new password"
              required
              data-testid="confirm-password-input"
            />
          </div>

          {error && (
            <p className="text-sm text-destructive font-medium" data-testid="force-password-error">
              {error}
            </p>
          )}

          <Button
            type="submit"
            className="w-full rounded-lg h-11"
            disabled={loading}
            data-testid="save-password-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save password & continue"}
          </Button>
        </form>

        <button
          type="button"
          onClick={logout}
          className="text-sm text-muted-foreground hover:text-foreground mt-6 flex items-center gap-1.5 mx-auto"
          data-testid="force-password-logout"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
      <Footer />
    </div>
  );
}
